import {
  Listing,
  MealSummary,
  MealSwap,
} from '@tovala/browser-apis-combinedapi'
import {
  getAllMenuComponentsMealIDs,
  getCommonMealProperties,
  getMenuComponentsStandardized,
  makeMenuComponentsStandardized,
  MenuComponentsStandardized,
  MenuComponentStandardizedFeedback,
  MenuComponentStandardizedMeal,
  MenuComponentStandardizedMealWithDecoupledAddOn,
} from '@tovala/browser-apis-menu-components'
import { MenuComponents, useMenu } from '@tovala/browser-apis-menu-delivery'
import { useQuery } from '@tanstack/react-query'
import { AnalyticsEvent, events } from 'analytics/events'
import { useUser } from 'contexts/user'
import { isEqual, throttle } from 'lodash-es'
import { useEffect, useMemo, useRef } from 'react'
import { track } from 'utils/analytics'
import { TAG_IDS } from 'utils/meals'
import { SelectedMealFilters } from 'types/internal'
import { convertToBaseMeal, getSelectedMealFiltersMetadata } from 'utils/menus'
import { v4 as uuidV4 } from 'uuid'

import { menuProductRatingQueries } from './queries/menuProductRatings'
import { MenuProductRating } from './service/combinedapi/menuProductRatings'

export function useResolvedMenuComponents({
  enableMenuTargeting,
  mealSelectionIDs,
  listings,
  mealSummaries,
  mealSwaps,
  orderAgainMeals,
  ratingsByMealID,
  subTermID,
  userID,
}: {
  enableMenuTargeting: boolean
  mealSelectionIDs: number[]
  listings: Listing[]
  mealSummaries: MealSummary[]
  mealSwaps: MealSwap[]
  orderAgainMeals: { id: number }[]
  ratingsByMealID: RatingsByMealID
  subTermID: string
  userID: number
}) {
  const {
    data: menuComponents,
    error: loadMenuError,
    isError: hasLoadMenuError,
  } = useMenu({
    subTermID,
    userID,
  })
  const components = menuComponents?.components ?? []

  let resolvedMenuComponents = getMenuComponentsStandardized({
    listings,
    meals: mealSummaries,
    menuComponents: components,
  })

  /**
   * If menu targeting is not enabled, make menu components for any missing meals
   * from meal summaries without MDS menu components so user sees all meals.
   * This will create menu components for all meals if MDS is returning no menu components.
   */
  if (!enableMenuTargeting) {
    const additionalComponents = makeMenuComponentsStandardized({
      meals: hasLoadMenuError
        ? mealSummaries
        : getMealsWithoutMenuComponents({
            menuComponents: components,
            mealSummaries,
          }),
      mealSwaps,
      specialEvent: undefined,
    })

    resolvedMenuComponents = [
      ...resolvedMenuComponents,
      ...additionalComponents,
    ]
  }

  // UI Editor does not currently add a feedback component so we insert one
  // at the end of the list of components
  if (resolvedMenuComponents.length > 0) {
    const feedbackComponent: MenuComponentStandardizedFeedback = {
      id: uuidV4(),
      properties: {
        options: [
          { label: '😠', value: 1 },
          { label: '😕', value: 2 },
          { label: '😐', value: 3 },
          { label: '😋', value: 4 },
          { label: '😍', value: 5 },
        ],
        subtitle: 'All feedback helps us improve our future menus',
        tableID: 'menu_feedback',
        title: 'How would you rate the menu for this week?',
      },
      type: 'menuFeedback' as const,
    }
    resolvedMenuComponents.push(feedbackComponent)
  }

  const orderAgainMealIDs = new Set(orderAgainMeals.map(({ id }) => id))
  const orderAgainCarouselChildren = getOrderAgainCarouselChildren({
    components: resolvedMenuComponents,
    mealSummaries,
    orderAgainMealIDs,
    mealSelectionIDs,
    ratingsByMealID,
  })

  // We won't show the order again carousel unless there's at least 1 order again meal
  if (orderAgainCarouselChildren.length > 0) {
    resolvedMenuComponents = resolvedMenuComponents.map((component) => {
      if (component.type === 'orderAgainCarousel') {
        return {
          ...component,
          properties: {
            ...component.properties,
            children: orderAgainCarouselChildren,
          },
        }
      }
      return component
    })
  } else {
    resolvedMenuComponents = resolvedMenuComponents.filter(
      (component) => component.type !== 'orderAgainCarousel',
    )
  }

  // Override imageTag to 'Order it Again' for previously-ordered meals.
  // Skip orderAgainCarousel — its children have dedicated UI and don't need this tag.
  resolvedMenuComponents = resolvedMenuComponents.map((component) =>
    component.type === 'orderAgainCarousel'
      ? component
      : applyOrderAgainImageTag(component, orderAgainMealIDs),
  )

  resolvedMenuComponents = resolvedMenuComponents.map(
    sortCarouselChildrenTallFirst,
  )

  return {
    resolvedMenuComponents,
    loadMenuError: enableMenuTargeting ? loadMenuError : null,
  }
}

export interface MealRating {
  // Chip IDs only. Resolve names with useMealReviewChips and reviewQueries.ratingChips.
  chips: string[]
  comment: string
  created: string
  stars: number
}

export type RatingsByMealID = Map<number, MealRating>

export function useMenuProductRatings({
  menuID,
}: {
  menuID: string | undefined
}) {
  const { user } = useUser()

  const { data } = useQuery(
    menuProductRatingQueries.list({ menuID, userID: user.id }),
  )

  const ratingsByMealID = useMemo(
    () => getRatingsByMealID(data?.menuProducts),
    [data],
  )

  return { ratingsByMealID }
}

export function getRatingsByMealID(
  menuProducts: MenuProductRating[] | undefined,
): RatingsByMealID {
  const ratingsByMealID: RatingsByMealID = new Map()

  menuProducts?.forEach(({ mealID, userRating }) => {
    if (mealID === null || !userRating || userRating.stars === null) {
      return
    }

    const existing = ratingsByMealID.get(mealID)
    if (existing && !isMoreRecent(userRating.created, existing.created)) {
      return
    }

    ratingsByMealID.set(mealID, {
      chips: userRating.chips,
      comment: userRating.comment,
      created: userRating.created,
      stars: userRating.stars,
    })
  })

  return ratingsByMealID
}

export function getBestRating(
  ratings: (MealRating | undefined)[],
): MealRating | undefined {
  return ratings.reduce<MealRating | undefined>((best, rating) => {
    if (!rating) {
      return best
    }
    if (!best || rating.stars > best.stars) {
      return rating
    }
    if (
      rating.stars === best.stars &&
      isMoreRecent(rating.created, best.created)
    ) {
      return rating
    }
    return best
  }, undefined)
}

function isMoreRecent(created: string, comparedTo: string) {
  return Date.parse(created) > Date.parse(comparedTo)
}

export function applyOrderAgainImageTag<
  C extends MenuComponentsStandardized[number],
>(component: C, orderAgainMealIDs: Set<number>): C {
  if (component.type === 'meal') {
    if (
      orderAgainMealIDs.has(component.properties.id) &&
      !component.properties.mealSummary.tags.some(
        (tag) => tag.id === TAG_IDS.FAMILY,
      )
    ) {
      return {
        ...component,
        properties: {
          ...component.properties,
          imageTag: { title: 'Order it Again' },
        },
      }
    }
    return component
  }

  if (component.type === 'mealWithDecoupledAddOn') {
    const { meal } = component.properties

    if (
      orderAgainMealIDs.has(meal.id) &&
      !meal.mealSummary.tags.some((tag) => tag.id === TAG_IDS.FAMILY)
    ) {
      return {
        ...component,
        properties: {
          ...component.properties,
          meal: { ...meal, imageTag: { title: 'Order it Again' } },
        },
      }
    }

    return component
  }

  if (
    component.type === 'textImageStack' ||
    component.type === 'mealCarousel'
  ) {
    return {
      ...component,
      properties: {
        ...component.properties,
        children: component.properties.children.map((child) =>
          applyOrderAgainImageTag(child, orderAgainMealIDs),
        ),
      },
    }
  }

  return component
}

export function findMenuComponentForOrderAgainMeal(
  mealID: number,
  components: MenuComponentsStandardized,
):
  | MenuComponentStandardizedMeal
  | MenuComponentStandardizedMealWithDecoupledAddOn
  | null {
  for (const component of components) {
    if (component.type === 'meal' && component.properties.id === mealID) {
      return component
    }
    if (
      component.type === 'mealWithDecoupledAddOn' &&
      component.properties.meal.id === mealID
    ) {
      return component
    }
    if (
      component.type === 'mealCarousel' ||
      component.type === 'textImageStack'
    ) {
      const childComponent = findMenuComponentForOrderAgainMeal(
        mealID,
        component.properties.children,
      )
      if (childComponent) {
        return childComponent
      }
    }
  }

  return null
}

export function getOrderAgainCarouselChildren({
  components,
  mealSummaries,
  orderAgainMealIDs,
  mealSelectionIDs,
  ratingsByMealID,
}: {
  components: MenuComponentsStandardized
  mealSummaries: MealSummary[]
  orderAgainMealIDs: Set<number>
  mealSelectionIDs: number[]
  ratingsByMealID: RatingsByMealID
}): (
  | MenuComponentStandardizedMeal
  | MenuComponentStandardizedMealWithDecoupledAddOn
)[] {
  const orderAgainMealSummaries = Array.from(orderAgainMealIDs)
    .map((mealID) => {
      const summary = mealSummaries.find((m) => m.id === mealID)
      return summary
    })
    .filter((summary): summary is MealSummary => summary !== undefined)

  const children = orderAgainMealSummaries.map(
    (meal) =>
      findMenuComponentForOrderAgainMeal(meal.id, components) ??
      makeStandaloneOrderAgainMealComponent(meal),
  )

  const visibleChildren = children.filter((component) => {
    const baseMeal = convertToBaseMeal(component).properties
    return (
      !isRatedPoorly(baseMeal.id, ratingsByMealID) &&
      !isSoldOutAndUnselected(baseMeal, mealSelectionIDs)
    )
  })

  // Tall cards first so they appear first in the carousel and we don't get a big
  // gap between the carousel and the next component. Within each group, the
  // original order (from orderAgainMealIDs) is preserved via a stable sort.
  return sortTallCardsFirst(visibleChildren)
}

function makeStandaloneOrderAgainMealComponent(
  meal: MealSummary,
): MenuComponentStandardizedMeal {
  return {
    id: `order-again-meal-${meal.id}`,
    properties: getCommonMealProperties({ meal }),
    type: 'meal',
  }
}

function isSoldOutAndUnselected(
  meal: MenuComponentStandardizedMeal['properties'],
  mealSelectionIDs: number[],
): boolean {
  return meal.mealSummary.isSoldOut && !mealSelectionIDs.includes(meal.id)
}

function sortTallCardsFirst<
  T extends
    | MenuComponentStandardizedMeal
    | MenuComponentStandardizedMealWithDecoupledAddOn,
>(children: readonly T[]): T[] {
  return [...children].sort((a, b) => {
    const aIsTall = a.type === 'mealWithDecoupledAddOn'
    const bIsTall = b.type === 'mealWithDecoupledAddOn'
    if (aIsTall && !bIsTall) {
      return -1
    }
    if (!aIsTall && bIsTall) {
      return 1
    }
    return 0
  })
}

export function sortCarouselChildrenTallFirst<
  C extends MenuComponentsStandardized[number],
>(component: C): C {
  if (component.type === 'mealCarousel') {
    return {
      ...component,
      properties: {
        ...component.properties,
        children: sortTallCardsFirst(component.properties.children),
      },
    }
  }

  return component
}

const POORLY_RATED_STARS_MAX = 2

function isRatedPoorly(
  mealID: number,
  ratingsByMealID: RatingsByMealID,
): boolean {
  const rating = ratingsByMealID.get(mealID)

  return rating !== undefined && rating.stars <= POORLY_RATED_STARS_MAX
}

function getMealsWithoutMenuComponents({
  menuComponents,
  mealSummaries,
}: {
  menuComponents: MenuComponents['components']
  mealSummaries: MealSummary[]
}) {
  const menuComponentsMealIDs = getAllMenuComponentsMealIDs({
    menuComponents,
  })

  return mealSummaries.filter((meal) => {
    return !menuComponentsMealIDs.has(meal.id)
  })
}

export function useTrackScrollDepth({
  maxSelections,
  numMeals,
  numSelections,
  selectedMealSort,
  termID,
  userID,
}: {
  maxSelections: number | undefined
  numMeals: number
  numSelections: number
  selectedMealSort: string
  termID: number
  userID: number
}) {
  useEffect(() => {
    let scrollDepth = 0

    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID })

    const trackScrollMenu = (event: AnalyticsEvent) => {
      track(event, {
        meal_selection_count: numSelections,
        meal_selection_max: maxSelections,
        term_id: termID,
        enabled_filters,
        enabled_filters_count,
        sort_order: selectedMealSort,
      })
    }

    const throttleTrackScrollsMenu25 = throttle(trackScrollMenu, 2000, {
      trailing: false,
    })
    const throttleTrackScrollsMenu50 = throttle(trackScrollMenu, 2000, {
      trailing: false,
    })
    const throttleTrackScrollsMenu75 = throttle(trackScrollMenu, 2000, {
      trailing: false,
    })
    const throttleTrackScrollsMenu100 = throttle(trackScrollMenu, 2000, {
      trailing: false,
    })

    const onMenuScroll = () => {
      if (numMeals) {
        const scrollY = window.scrollY
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight
        const newScrollDepth = Math.round((scrollY / scrollHeight) * 100)
        scrollDepth = newScrollDepth

        if (scrollDepth === 25) {
          throttleTrackScrollsMenu25(events.SCROLLS_MENU_25)
        }

        if (scrollDepth === 50) {
          throttleTrackScrollsMenu50(events.SCROLLS_MENU_50)
        }

        if (scrollDepth === 75) {
          throttleTrackScrollsMenu75(events.SCROLLS_MENU_75)
        }

        if (scrollDepth === 100) {
          throttleTrackScrollsMenu100(events.SCROLLS_MENU_100)
        }
      }
    }
    window.addEventListener('scroll', onMenuScroll)

    return () => {
      window.removeEventListener('scroll', onMenuScroll)
    }
  }, [maxSelections, numMeals, numSelections, selectedMealSort, termID, userID])
}

export function useScrollToMenuTopOnFiltersChange({
  isFiltersSidebarOpen,
  scrollToMenuTop,
  selectedMealFilters,
}: {
  isFiltersSidebarOpen: boolean
  scrollToMenuTop(): void
  selectedMealFilters: SelectedMealFilters
}) {
  const handledFiltersRef = useRef(selectedMealFilters)

  // Wait for the sidebar to close because the iOS dialog scroll lock restores the pre-open scroll position on unlock.
  // Filters restored on load pass through harmlessly: the user is at the top, so scrollToMenuTop skips the scroll.
  useEffect(() => {
    if (isFiltersSidebarOpen) {
      return
    }

    if (!isEqual(handledFiltersRef.current, selectedMealFilters)) {
      handledFiltersRef.current = selectedMealFilters
      scrollToMenuTop()
    }
  }, [isFiltersSidebarOpen, scrollToMenuTop, selectedMealFilters])
}
